import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { Megaphone } from "lucide-react"
import { useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import {
	type AudienceFilter,
	type AudienceSegment,
	type AudienceStateFilter,
	audienceKeys,
	contextChips,
	downloadAudienceCsv,
	listAudience,
	plural,
	saveBlob,
} from "@/entities/audience"
import { botBuilderKeys, draftCache, getBot } from "@/entities/bot-builder"
import { useSessionRole } from "@/entities/session"
import { AudiencePills, describeFilter, pickSegment } from "@/features/filter-audience"
import { BulkGroupMenu } from "@/features/manage-groups"
import { SavedFilterChips, SaveFilterButton } from "@/features/save-audience-filter"
import { useStartBroadcast } from "@/features/send-broadcast"
import { useDebounced } from "@/shared/hooks/use-debounced"
import { useUrlState } from "@/shared/hooks/use-url-state"
import { AccessDenied } from "@/shared/ui/access-denied"
import { Button } from "@/shared/ui/button"
import { AUDIENCE_SORT_OPTIONS, AudienceListSection } from "@/widgets/audience-list"
import { useBotWorkspace, WorkspaceSection } from "@/widgets/bot-workspace"

const PAGE_SIZE = 50
/** Столько же принимает api в sessionIds. */
const MAX_PICKED = 1000
const PERIOD_PARAMS = {
	joinedAfter: true,
	activeAfter: true,
	failedInBroadcastId: true,
	excludeBroadcastId: true,
} as const
const TITLE = "Пользователи"
const DAY_MS = 24 * 60 * 60 * 1000
const daysAgo = (days: number) => new Date(Date.now() - days * DAY_MS).toISOString()

/** Пользователи стороннего бота: поиск, фильтры по статусу и ответам, выгрузка CSV. */
export function AudiencePage() {
	const { botId = "" } = useParams()
	const navigate = useNavigate()
	const location = useLocation()
	const { isAdmin, isModerator } = useSessionRole()
	const [search, setSearch] = useUrlState("q")
	const [stateParam, setStateParam] = useUrlState("state")
	const state = (["waiting", "idle", "blocked"].includes(stateParam) ? stateParam : undefined) as
		| AudienceStateFilter
		| undefined
	const setState = (value: AudienceStateFilter | undefined) => setStateParam(value ?? "")
	const [segmentParam, setSegmentParam] = useUrlState("segment")
	const segment = parseSegment(segmentParam)
	const setSegment = (value: AudienceSegment) => {
		const picked = pickSegment(value)
		setSegmentParam(Object.keys(picked).length ? JSON.stringify(picked) : "")
	}
	// Из метки и из блока воронки: ?link=код, ?passed=id или ?stuck=id, ?node=название блока.
	const [entryLink, setEntryLink] = useUrlState("link")
	const scenario = useBotWorkspace().draft?.draft
	const entryLinks = scenario?.entryLinks ?? []
	const [tagId, setTagId] = useUrlState("group")
	const tags = draftCache.pickableTags(scenario?.tags ?? [], tagId ? [tagId] : [])
	const [passedNode, setPassedNode] = useUrlState("passed")
	const [stuckNode, setStuckNode] = useUrlState("stuck")
	const [nodeLabel, setNodeLabel] = useUrlState("node")
	// Из сводки бота и истории рассылок: период и прошлая рассылка.
	const [joinedAfter, setJoinedAfter] = useUrlState("joinedAfter")
	const [activeAfter, setActiveAfter] = useUrlState("activeAfter")
	const [failedIn, setFailedIn] = useUrlState("failedIn")
	const [excludeBroadcast, setExcludeBroadcast] = useUrlState("exclude")
	const [sortParam, setSortParam] = useUrlState("sort")
	const sort = AUDIENCE_SORT_OPTIONS.some((option) => option.value === sortParam)
		? sortParam
		: "active"
	const setSort = (value: string) => setSortParam(value === "active" ? "" : value)
	const [exporting, setExporting] = useState(false)
	const [picked, setPicked] = useState<ReadonlySet<string>>(new Set())
	const startBroadcast = useStartBroadcast(botId)
	const debouncedSearch = useDebounced(search.trim(), 300)

	const filter: AudienceFilter = {
		...(debouncedSearch ? { search: debouncedSearch } : {}),
		...(state ? { state } : {}),
		...(entryLink ? { entryLink } : {}),
		...(tagId ? { tagId } : {}),
		...(passedNode ? { passedNodeId: passedNode } : {}),
		...(stuckNode ? { stuckNodeId: stuckNode } : {}),
		...(joinedAfter ? { joinedAfter } : {}),
		...(activeAfter ? { activeAfter } : {}),
		...(failedIn ? { failedInBroadcastId: failedIn } : {}),
		...(excludeBroadcast ? { excludeBroadcastId: excludeBroadcast } : {}),
		...segment,
	}
	const filtered = Object.keys(filter).length > 0

	const bot = useQuery({
		queryKey: botBuilderKeys.bot(botId),
		queryFn: () => getBot(botId),
		enabled: isModerator && !!botId,
	})
	const audience = useInfiniteQuery({
		queryKey: [...audienceKeys.list(botId, filter), sort],
		queryFn: ({ pageParam }) =>
			listAudience(botId, filter, { skip: pageParam, take: PAGE_SIZE, sort }),
		initialPageParam: 0,
		getNextPageParam: (last, pages) => {
			const loaded = pages.reduce((sum, page) => sum + page.items.length, 0)
			return loaded < last.total ? loaded : undefined
		},
		placeholderData: keepPreviousData,
		enabled: isModerator && !!botId,
	})

	if (!isModerator) return <AccessDenied title={TITLE} />

	const pages = audience.data?.pages ?? []
	const members = pages.flatMap((page) => page.items)
	const total = pages[0]?.total ?? 0
	const variables = pages[0]?.variables ?? []

	const exportCsv = async () => {
		setExporting(true)
		try {
			const { blob, fileName } = await downloadAudienceCsv(botId, {
				...filter,
				search: search.trim() || undefined,
			})
			saveBlob(blob, fileName)
		} catch (error) {
			toast.error(extractApiErrorMessage(error, "Не удалось выгрузить CSV"))
		} finally {
			setExporting(false)
		}
	}

	const resetFilters = () => {
		setSearch("")
		setState(undefined)
		setSegment({})
		setEntryLink("")
		setTagId("")
		setPassedNode("")
		setStuckNode("")
		setNodeLabel("")
		setJoinedAfter("")
		setActiveAfter("")
		setFailedIn("")
		setExcludeBroadcast("")
	}

	const togglePicked = (ids: string[], on: boolean) => {
		const next = new Set(picked)
		for (const id of ids) {
			if (!on) next.delete(id)
			else if (next.size < MAX_PICKED) next.add(id)
		}
		if (on && next.size === MAX_PICKED) toast.info(`Отметить можно до ${MAX_PICKED} человек`)
		setPicked(next)
	}
	const pickedFilter: AudienceFilter = { sessionIds: [...picked] }
	const selectionBar =
		picked.size > 0 ? (
			<>
				<span className="text-sm">
					Отмечено: <span className="tabular-nums">{picked.size}</span>
				</span>
				<BulkGroupMenu
					botId={botId}
					filter={pickedFilter}
					total={picked.size}
					tags={tags}
					picked
					onDone={() => setPicked(new Set())}
				/>
				<Button
					variant="outline"
					className="h-10 rounded-xl"
					disabled={startBroadcast.isPending}
					onClick={() =>
						startBroadcast.mutate({
							name: describeFilter(pickedFilter, tags),
							filter: pickedFilter,
						})
					}
				>
					<Megaphone />
					<span className="hidden sm:inline">Рассылка отмеченным</span>
				</Button>
				<Button variant="ghost" className="h-10 rounded-xl" onClick={() => setPicked(new Set())}>
					Снять отметку
				</Button>
			</>
		) : null

	const blockName = nodeLabel ? `«${nodeLabel}»` : "выбранный блок"
	const clearBlock = (clear: (value: string) => void) => () => {
		clear("")
		setNodeLabel("")
	}
	const filters = (
		<AudiencePills
			value={segment}
			onChange={setSegment}
			variables={variables}
			extras={{
				state,
				onStateChange: setState,
				entryLinks,
				entryLink,
				onEntryLinkChange: setEntryLink,
				tags,
				tagId,
				onTagChange: setTagId,
				fixed: [
					...(passedNode
						? [
								{
									id: "passed",
									label: `Прошли блок ${blockName}`,
									onClear: clearBlock(setPassedNode),
								},
							]
						: []),
					...(stuckNode
						? [
								{
									id: "stuck",
									label: `Застряли на блоке ${blockName}`,
									onClear: clearBlock(setStuckNode),
								},
							]
						: []),
					...contextChips(filter)
						.filter((chip) => chip.key in PERIOD_PARAMS)
						.map((chip) => ({
							id: chip.key,
							label: chip.label,
							onClear: () =>
								({
									joinedAfter: setJoinedAfter,
									activeAfter: setActiveAfter,
									failedInBroadcastId: setFailedIn,
									excludeBroadcastId: setExcludeBroadcast,
								})[chip.key as keyof typeof PERIOD_PARAMS]?.(""),
						})),
				],
			}}
		/>
	)

	return (
		<WorkspaceSection
			title={TITLE}
			subtitle={
				bot.data
					? `@${bot.data.username}${audience.data ? ` · ${total} ${plural(total, ["пользователь", "пользователя", "пользователей"])}${filtered ? " по фильтру" : ""}` : ""}`
					: undefined
			}
		>
			<AudienceListSection
				search={search}
				onSearchChange={setSearch}
				variables={variables}
				exporting={exporting}
				onExportCsv={isAdmin ? () => void exportCsv() : undefined}
				total={total}
				filtered={filtered}
				isPlaceholderData={audience.isPlaceholderData}
				listKey={`${JSON.stringify(filter)}-${sort}`}
				isLoading={audience.isLoading}
				isError={audience.isError}
				onRetry={() => void audience.refetch()}
				members={members}
				botUsername={bot.data?.username}
				onResetFilters={resetFilters}
				onOpenMember={(memberId) => navigate(`/admin/bots/${botId}/analytics/users/${memberId}`)}
				hasNextPage={audience.hasNextPage}
				isFetchingNextPage={audience.isFetchingNextPage}
				onFetchNextPage={() => void audience.fetchNextPage()}
				sort={sort}
				onSortChange={setSort}
				onBroadcast={() =>
					startBroadcast.mutate({
						name: filtered ? describeFilter(filter, tags) : "Рассылка всем",
						filter,
					})
				}
				broadcasting={startBroadcast.isPending}
				filters={filters}
				saved={
					<SavedFilterChips
						botId={botId}
						onApply={(next) =>
							navigate(
								{ pathname: location.pathname, search: filterToSearch(next) },
								{ replace: true },
							)
						}
					/>
				}
				saveCurrent={
					<SaveFilterButton
						botId={botId}
						filter={filter}
						suggestedName={shortName(describeFilter(filter, tags))}
					/>
				}
				selectedIds={picked}
				onToggleMember={(memberId) => togglePicked([memberId], !picked.has(memberId))}
				onToggleAll={() =>
					togglePicked(
						members.map((member) => member.id),
						!members.every((member) => picked.has(member.id)),
					)
				}
				selectionBar={selectionBar}
				actions={<BulkGroupMenu botId={botId} filter={filter} total={total} tags={tags} />}
				presets={[
					{ label: "Ждут ответа", onSelect: () => setState("waiting") },
					{ label: "Новые за неделю", onSelect: () => setJoinedAfter(daysAgo(7)) },
					{ label: "Писали за сутки", onSelect: () => setActiveAfter(daysAgo(1)) },
					{
						label: "Давно не смотрели материалы",
						onSelect: () => setSegment({ notReadDays: 30 }),
					},
				]}
			/>
		</WorkspaceSection>
	)
}

function parseSegment(raw: string): AudienceSegment {
	if (!raw) return {}
	try {
		const value: unknown = JSON.parse(raw)
		return value && typeof value === "object" ? pickSegment(value as AudienceSegment) : {}
	} catch {
		return {}
	}
}

/** Обратно из условий в адрес страницы: так сохранённая выборка открывается как обычный фильтр. */
function filterToSearch(filter: AudienceFilter): string {
	const params = new URLSearchParams()
	const put = (key: string, value: string | undefined) => {
		if (value) params.set(key, value)
	}
	put("q", filter.search)
	put("state", filter.state)
	put("group", filter.tagId)
	put("link", filter.entryLink)
	put("passed", filter.passedNodeId)
	put("stuck", filter.stuckNodeId)
	put("joinedAfter", filter.joinedAfter)
	put("activeAfter", filter.activeAfter)
	put("failedIn", filter.failedInBroadcastId)
	put("exclude", filter.excludeBroadcastId)
	const segment = pickSegment(filter)
	if (Object.keys(segment).length > 0) params.set("segment", JSON.stringify(segment))
	return params.toString()
}

/** «Все пользователи бота, в группе «горячие»» → «В группе «горячие»»: имя выборки короче. */
function shortName(description: string): string {
	const rest = description.replace(/^Все пользователи бота,?\s*/, "")
	return rest ? rest.charAt(0).toUpperCase() + rest.slice(1) : description
}
